import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import type { AccommodationDto } from '@hcr/api/consumer'
import type { DestinationDto } from '@hcr/api/optimizely'
import {
  find,
  flow,
  getFromSessionStorage,
  getPropertyValue,
  head,
  isNotNull,
  isNotUndefined,
  isUndefined,
  logger,
  removeFromSessionStorage,
  setToSessionStorage,
  sort,
} from '@hcr/utils'

import {
  useAccommodationDetailsQuery,
  useAccommodationsQuery,
  useActivitiesQuery,
  useDestinationDetailsQuery,
  useDestinationsQuery,
  useIdToken,
  useSingleTicketsQuery,
} from '../hooks'
import type { UseCompoundQueryResult } from '../models'
import { compareDates, isBookingIn, isDestinationOf } from '../utils'

interface Holiday {
  accommodation: AccommodationDto | undefined // Consumer API
  destination: DestinationDto | undefined // Optimizely API
}

const DEFAULT_HOLIDAY: Holiday = {
  accommodation: undefined,
  destination: undefined,
}

const HOLIDAY_CONFIG = {
  destinationStorageKeyName: 'holiday-destination-id',
  accommodationStorageKeyName: 'holiday-accommodation-id',
} as const

interface HolidayContext extends UseCompoundQueryResult<Holiday> {
  setAccommodation: (accommodation: AccommodationDto) => void
  setDestination: (destination: DestinationDto) => void
}

const HolidayContext = createContext<HolidayContext>({
  data: DEFAULT_HOLIDAY,
  isError: false,
  isPending: true,
  isSuccess: false,
  setAccommodation: () => void undefined,
  setDestination: () => void undefined,
})

enum TRIGGER {
  INIT,
  ACCOMMODATION,
  DESTINATION,
  NONE,
}

type HolidayProviderProps = PropsWithChildren

export const HolidayProvider: FC<HolidayProviderProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated()
  const { instance } = useMsal()
  const idToken = useIdToken()

  const [trigger, setTrigger] = useState<TRIGGER>(TRIGGER.INIT)
  const [accommodation, setAccommodation] = useState<AccommodationDto | undefined>(DEFAULT_HOLIDAY.accommodation)
  const [destination, setDestination] = useState<DestinationDto | undefined>(DEFAULT_HOLIDAY.destination)

  const [savedAccommodationId, setSavedAccommodationId] = useState<string | null>(() =>
    getFromSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName)
  )

  const [savedDestinationId, setSavedDestinationId] = useState<string | null>(() =>
    getFromSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName)
  )

  useEffect(() => {
    const callbackId = instance.addEventCallback(event => {
      if (event.eventType === 'msal:logoutSuccess' || event.eventType === 'msal:logoutFailure') {
        clearTrigger()
      }
    })

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [instance])

  const setAccommodationTrigger = (accommodation: AccommodationDto) => {
    setAccommodation(accommodation)
    setDestination(undefined)
    setTrigger(TRIGGER.ACCOMMODATION)

    setToSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName, String(accommodation.accommodation_id))
    removeFromSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName)
    setSavedAccommodationId(String(accommodation.accommodation_id))
    setSavedDestinationId(null)
  }

  const setDestinationTrigger = (destination: DestinationDto) => {
    setAccommodation(undefined)
    setDestination(destination)
    setTrigger(TRIGGER.DESTINATION)

    setToSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName, destination.destinationId)
    removeFromSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName)
    setSavedDestinationId(destination.destinationId)
    setSavedAccommodationId(null)
  }

  const clearTrigger = () => {
    setAccommodation(undefined)
    setDestination(undefined)
    setTrigger(TRIGGER.NONE)

    removeFromSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName)
    removeFromSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName)
    setSavedDestinationId(null)
    setSavedAccommodationId(null)
  }

  const earliestAccommodation = useEarliestAccommodation()
  const firstTicketDestination = useFirstTicketDestination()

  const accommodationIn = useAccommodationIn(destination?.destinationId ?? null)
  const destinationOf = useDestinationOf(accommodation?.accommodation_id ?? null)

  const savedAccommodationDetails = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(savedAccommodationId) },
    { enabled: isNotNull(savedAccommodationId) && isNotNull(idToken) }
  )

  const savedDestinationDetails = useDestinationDetailsQuery(
    { destinationId: String(savedDestinationId) },
    { enabled: isNotNull(savedDestinationId) }
  )

  useEffect(() => {
    if (
      savedAccommodationDetails.isSuccess &&
      savedAccommodationDetails.data &&
      !accommodation &&
      trigger === TRIGGER.INIT
    ) {
      setAccommodation(savedAccommodationDetails.data)
      setTrigger(TRIGGER.ACCOMMODATION)
    }
  }, [savedAccommodationDetails.isSuccess, savedAccommodationDetails.data, accommodation, trigger])

  useEffect(() => {
    if (savedDestinationDetails.isSuccess && savedDestinationDetails.data && !destination && trigger === TRIGGER.INIT) {
      setDestination(savedDestinationDetails.data)
      setTrigger(TRIGGER.DESTINATION)
    }
  }, [savedDestinationDetails.isSuccess, savedDestinationDetails.data, destination, trigger])

  useEffect(() => {
    if (savedAccommodationDetails.isError || (savedAccommodationDetails.isSuccess && !savedAccommodationDetails.data)) {
      removeFromSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName)
      setSavedAccommodationId(null)
    }
  }, [savedAccommodationDetails.isError, savedAccommodationDetails.isSuccess, savedAccommodationDetails.data])

  useEffect(() => {
    if (savedDestinationDetails.isError || (savedDestinationDetails.isSuccess && !savedDestinationDetails.data)) {
      removeFromSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName)
      setSavedDestinationId(null)
    }
  }, [savedDestinationDetails.isError, savedDestinationDetails.isSuccess, savedDestinationDetails.data])

  useEffect(() => {
    if (
      trigger === TRIGGER.INIT &&
      isAuthenticated &&
      earliestAccommodation.isSuccess &&
      firstTicketDestination.isSuccess
    ) {
      const hasSavedAccommodation = savedAccommodationId && savedAccommodationDetails.isSuccess
      const hasSavedDestination = savedDestinationId && savedDestinationDetails.isSuccess
      const savedAccommodationStillLoading = savedAccommodationId && savedAccommodationDetails.isPending
      const savedDestinationStillLoading = savedDestinationId && savedDestinationDetails.isPending

      if (savedAccommodationStillLoading || savedDestinationStillLoading) {
        return
      }

      if (hasSavedAccommodation && savedAccommodationDetails.data) {
        setAccommodation(savedAccommodationDetails.data)
        setTrigger(TRIGGER.ACCOMMODATION)
        return
      }

      if (hasSavedDestination && savedDestinationDetails.data) {
        setDestination(savedDestinationDetails.data)
        setTrigger(TRIGGER.DESTINATION)
        return
      }

      if (!savedAccommodationId && !savedDestinationId) {
        if (isNotUndefined(earliestAccommodation.data)) {
          setAccommodation(earliestAccommodation.data)
          setTrigger(TRIGGER.ACCOMMODATION)
          return
        }

        if (isNotUndefined(firstTicketDestination.data)) {
          setDestination(firstTicketDestination.data)
          setTrigger(TRIGGER.DESTINATION)
          return
        }

        clearTrigger()
      }
    }
  }, [
    trigger,
    isAuthenticated,
    savedAccommodationId,
    savedDestinationId,
    savedAccommodationDetails.isSuccess,
    savedAccommodationDetails.isPending,
    savedAccommodationDetails.data,
    savedDestinationDetails.isSuccess,
    savedDestinationDetails.isPending,
    savedDestinationDetails.data,
    earliestAccommodation.isSuccess,
    earliestAccommodation.data,
    firstTicketDestination.isSuccess,
    firstTicketDestination.data,
  ])

  useEffect(() => {
    if (trigger === TRIGGER.ACCOMMODATION) {
      if (
        isAuthenticated &&
        earliestAccommodation.isSuccess &&
        isNotUndefined(earliestAccommodation.data) &&
        destinationOf.isSuccess &&
        isUndefined(destinationOf.data)
      ) {
        logger.warn(
          `Destination details are not available for resort_id: ${earliestAccommodation.data?.resort_id}, check if provided mokkiId or hotelId are correct`
        )

        clearTrigger()
      }
    }
  }, [
    trigger,
    isAuthenticated,
    earliestAccommodation.isSuccess,
    earliestAccommodation.data,
    destinationOf.isSuccess,
    destinationOf.data,
  ])

  const data = useMemo<Holiday>(() => {
    if (isAuthenticated) {
      if (trigger === TRIGGER.ACCOMMODATION && destinationOf.isSuccess && isNotUndefined(destinationOf.data)) {
        return { accommodation, destination: destinationOf.data }
      }

      if (trigger === TRIGGER.DESTINATION && accommodationIn.isSuccess && isNotUndefined(accommodationIn.data)) {
        return { accommodation: accommodationIn.data, destination }
      }
    }

    return { accommodation, destination }
  }, [
    accommodation,
    accommodationIn.data,
    accommodationIn.isSuccess,
    destination,
    destinationOf.data,
    destinationOf.isSuccess,
    isAuthenticated,
    trigger,
  ])

  return (
    <HolidayContext.Provider
      value={{
        data,
        isError:
          isAuthenticated &&
          ((trigger === TRIGGER.INIT && earliestAccommodation.isError) ||
            (trigger === TRIGGER.INIT && firstTicketDestination.isError) ||
            (trigger === TRIGGER.ACCOMMODATION && destinationOf.isError) ||
            (trigger === TRIGGER.ACCOMMODATION && destinationOf.isSuccess && isUndefined(destinationOf.data)) ||
            (trigger === TRIGGER.DESTINATION && accommodationIn.isError)),
        isPending:
          isAuthenticated &&
          ((trigger === TRIGGER.INIT && earliestAccommodation.isPending) ||
            (trigger === TRIGGER.INIT && firstTicketDestination.isPending) ||
            (trigger === TRIGGER.ACCOMMODATION && destinationOf.isPending) ||
            (trigger === TRIGGER.DESTINATION && accommodationIn.isPending)),
        isSuccess:
          !isAuthenticated ||
          (trigger === TRIGGER.ACCOMMODATION && destinationOf.isSuccess) ||
          (trigger === TRIGGER.DESTINATION && accommodationIn.isSuccess) ||
          trigger === TRIGGER.NONE,
        setAccommodation: setAccommodationTrigger,
        setDestination: setDestinationTrigger,
      }}
    >
      {children}
    </HolidayContext.Provider>
  )
}

export const useHoliday = (): HolidayContext => {
  const context = useContext(HolidayContext)

  if (isUndefined(context)) {
    throw new Error('useHoliday must be used within HolidayProvider')
  }

  return context
}

const useEarliestAccommodation = () => {
  const idToken = useIdToken()

  return useAccommodationsQuery(
    { idToken: String(idToken) },
    {
      select: flow(sort({ comparer: compareDates, asc: getPropertyValue('start_date') }), head),
      enabled: isNotNull(idToken),
    }
  )
}

const useFirstTicketDestination = (): UseCompoundQueryResult<DestinationDto | undefined> => {
  const idToken = useIdToken()

  const singleTicket = useSingleTicketsQuery(
    { idToken: String(idToken) },
    {
      select: head,
      enabled: isNotNull(idToken),
    }
  )

  const activity = useActivitiesQuery(
    { idToken: String(idToken) },
    {
      select: head,
      enabled: isNotNull(idToken),
    }
  )

  const ticket = singleTicket.data || activity.data

  const destination = useDestinationsQuery({
    select: flow(getPropertyValue('destinations'), find(isDestinationOf(ticket))),
    enabled: singleTicket.isSuccess,
  })

  return {
    data: destination.data,
    isError: singleTicket.isError || destination.isError || activity.isError,
    isPending:
      singleTicket.isPending ||
      (singleTicket.isSuccess && destination.isPending) ||
      activity.isPending ||
      (activity.isSuccess && activity.isPending),
    isSuccess: singleTicket.isSuccess && destination.isSuccess && activity.isSuccess,
  }
}

const useAccommodationIn = (destinationId: string | null): UseCompoundQueryResult<AccommodationDto | undefined> => {
  const idToken = useIdToken()

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(destinationId) },
    { enabled: isNotNull(destinationId) }
  )

  const accommodation = useAccommodationsQuery(
    { idToken: String(idToken) },
    {
      select: flow(
        sort({ comparer: compareDates, asc: getPropertyValue('start_date') }),
        find(isBookingIn(destinationDetails.data))
      ),
      enabled: isNotNull(idToken) && destinationDetails.isSuccess,
    }
  )

  return {
    data: accommodation.data,
    isError: destinationDetails.isError || accommodation.isError,
    isPending: destinationDetails.isPending || (destinationDetails.isSuccess && accommodation.isPending),
    isSuccess: destinationDetails.isSuccess && accommodation.isSuccess,
  }
}

const useDestinationOf = (accommodationId: number | null): UseCompoundQueryResult<DestinationDto | undefined> => {
  const idToken = useIdToken()

  const accommodation = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(accommodationId) },
    { enabled: isNotNull(idToken) && isNotNull(accommodationId) }
  )

  const destination = useDestinationsQuery({
    select: flow(getPropertyValue('destinations'), find(isDestinationOf(accommodation.data))),
    enabled: accommodation.isSuccess,
  })

  return {
    data: destination.data,
    isError: accommodation.isError || destination.isError,
    isPending: accommodation.isPending || (accommodation.isSuccess && destination.isPending),
    isSuccess: accommodation.isSuccess && destination.isSuccess,
  }
}
